import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  mergeClasses,
  Spinner,
  Tab,
  TabList,
} from "@fluentui/react-components";
import {
  Add12Regular,
  Checkmark12Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Dismiss20Regular,
  DocumentFolder16Regular,
  DocumentText16Regular,
  DocumentText20Regular,
} from "@fluentui/react-icons";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ITag } from "../../../../models/ITag";
import {
  useSharedBadgeStyles,
  useSharedButtonStyles,
  useSharedCloseButtonStyles,
  useSharedModalStyles,
  useSharedSectionStyles,
  useSharedTextStyles,
} from "../../../../theme/sharedStyles";
import { IWorkspaceLibraryDocument } from "../../../../components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { useWorkspaceManageContentStyles } from "./WorkspaceManageContent.styles";
import {
  IKnowledgeComponentGroup,
  IWorkspaceManageContentProps,
  ITabContentProps,
  WORKSPACE_MANAGE_CONTENT_TABS,
} from "./WorkspaceManageContent.types";

const groupKnowledgeComponentsByCategory = (
  knowledgeComponents: ITag[],
): IKnowledgeComponentGroup[] => {
  const groups = knowledgeComponents.reduce(
    (acc, item) => {
      const categoryName = item.category?.title || "Uncategorized";
      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }
      acc[categoryName].push(item);
      return acc;
    },
    {} as Record<string, ITag[]>,
  );

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([categoryName, items]) => ({
      categoryName,
      items: items.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    }));
};

const KnowledgeComponentsTab: React.FC<
  ITabContentProps & { onStatusMessage?: (message: string) => void }
> = ({
  knowledgeComponents,
  isLoadingKnowledgeComponents,
  selectedKnowledgeComponents,
  onKnowledgeComponentsChange,
  onStatusMessage = () => {},
}) => {
  const styles = useWorkspaceManageContentStyles();
  const sectionStyles = useSharedSectionStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();
  const groupedComponents = useMemo(
    () => groupKnowledgeComponentsByCategory(knowledgeComponents),
    [knowledgeComponents],
  );

  const handleRemoveSelectedComponent = useCallback(
    (componentId: string | undefined) => {
      if (!componentId) return;
      const component = selectedKnowledgeComponents.find(
        (comp) => comp.id === componentId,
      );
      onKnowledgeComponentsChange(
        selectedKnowledgeComponents.filter((comp) => comp.id !== componentId),
      );
      if (component) {
        onStatusMessage(
          `${component.displayName ?? component.name} removed from Selected Knowledge Components`,
        );
      }
    },
    [selectedKnowledgeComponents, onKnowledgeComponentsChange, onStatusMessage],
  );

  const handleToggleKnowledgeComponent = useCallback(
    (component: ITag) => {
      const isSelected = selectedKnowledgeComponents.some(
        (comp) => comp.id === component.id,
      );
      if (isSelected) {
        onKnowledgeComponentsChange(
          selectedKnowledgeComponents.filter(
            (comp) => comp.id !== component.id,
          ),
        );
        onStatusMessage(
          `${component.displayName ?? component.name} removed from Selected Knowledge Components`,
        );
      } else {
        onKnowledgeComponentsChange([
          ...selectedKnowledgeComponents,
          component,
        ]);
        onStatusMessage(
          `${component.displayName ?? component.name} added to Selected Knowledge Components`,
        );
      }
    },
    [selectedKnowledgeComponents, onKnowledgeComponentsChange, onStatusMessage],
  );

  return (
    <>
      <div className={styles.descriptionBox}>
        <p className={styles.descriptionBoxText}>
          Select a knowledge component when it warrants closer attention — it is
          consulted directly and in full for this conversation.
        </p>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Selected Knowledge Components
          </h3>
        </div>
        <div className={styles.focusTray}>
          {selectedKnowledgeComponents.length === 0 ? (
            <span className={styles.emptySelectedState}>
              No knowledge components selected
            </span>
          ) : (
            selectedKnowledgeComponents.map((component) => (
              <div
                key={component.id ?? component.name}
                className={mergeClasses(
                  styles.focusChipBase,
                  styles.focusChipWorkspace,
                )}
              >
                <DocumentText16Regular
                  className={styles.selectedTagIcon}
                  aria-hidden="true"
                />
                <span>{component.name}</span>
                <button
                  className={mergeClasses(
                    closeButtonStyles.closeButton,
                    styles.removeButton,
                  )}
                  onClick={() => handleRemoveSelectedComponent(component.id)}
                  aria-label={`Remove ${component.displayName ?? component.name} from selection`}
                >
                  <Dismiss20Regular className={styles.removeTagIcon} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Available Knowledge Components
          </h3>
        </div>
        {isLoadingKnowledgeComponents ? (
          <div className={styles.loadingContainer}>
            <Spinner label="Loading knowledge components..." size="medium" />
          </div>
        ) : groupedComponents.length === 0 ? (
          <span className={styles.emptySelectedState}>
            No knowledge components are available.
          </span>
        ) : (
          <div className={styles.accordionContainer}>
            <Accordion multiple collapsible>
              {groupedComponents.map((group) => (
                <AccordionItem
                  key={group.categoryName}
                  value={group.categoryName}
                >
                  <AccordionHeader className={styles.accordionHeader}>
                    <span className={styles.accordionHeaderContent}>
                      {group.categoryName}
                    </span>
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanel}>
                    <div className={styles.availableItemsList}>
                      {group.items.map((component) => {
                        const isSelected = selectedKnowledgeComponents.some(
                          (comp) => comp.id === component.id,
                        );
                        return (
                          <div
                            key={component.id ?? component.name}
                            className={mergeClasses(
                              styles.selectableItem,
                              isSelected && styles.selectableItemSelected,
                            )}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              handleToggleKnowledgeComponent(component)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleToggleKnowledgeComponent(component);
                              }
                            }}
                            aria-pressed={isSelected}
                            aria-label={
                              isSelected
                                ? `Remove ${component.name} from selection`
                                : `Add ${component.name} to selection`
                            }
                          >
                            <DocumentText20Regular aria-hidden="true" />
                            <div className={styles.selectableItemContent}>
                              <div className={styles.selectableItemText}>
                                {component.name}
                              </div>
                            </div>
                            {isSelected ? (
                              <Checkmark12Regular aria-hidden="true" />
                            ) : (
                              <Add12Regular aria-hidden="true" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </div>
    </>
  );
};

const WorkspaceDocumentsTab: React.FC<
  ITabContentProps & { onStatusMessage?: (message: string) => void }
> = ({
  workspaceDocuments,
  selectedWorkspaceDocuments,
  onWorkspaceDocumentsChange,
  uploadedDocuments,
  onUploadedDocumentsChange,
  workspaceId,
  isLoadingWorkspaceDocuments,
  onStatusMessage = () => {},
}) => {
  const styles = useWorkspaceManageContentStyles();
  const sectionStyles = useSharedSectionStyles();
  const textStyles = useSharedTextStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();

  const handleRemoveWorkspaceDocument = useCallback(
    (filename: string) => {
      const document = selectedWorkspaceDocuments.find(
        (doc) => doc.filename === filename,
      );
      onWorkspaceDocumentsChange(
        selectedWorkspaceDocuments.filter((doc) => doc.filename !== filename),
      );
      if (document) {
        onStatusMessage(
          `${document.originalFilename ?? document.filename} removed from Selected Workspace Documents`,
        );
      }
    },
    [selectedWorkspaceDocuments, onWorkspaceDocumentsChange, onStatusMessage],
  );

  const handleRemoveUploadedDocument = useCallback(
    (doc: { id?: string; filename: string }) => {
      onUploadedDocumentsChange(
        (uploadedDocuments ?? []).filter(
          (d) =>
            !(
              (d.id !== undefined && doc.id !== undefined && d.id === doc.id) ||
              (d.id === undefined && d.filename === doc.filename)
            ),
        ),
      );
      onStatusMessage(
        `${doc.filename} removed from Selected Workspace Documents`,
      );
    },
    [uploadedDocuments, onUploadedDocumentsChange, onStatusMessage],
  );

  const handleToggleWorkspaceDocument = useCallback(
    (document: IWorkspaceLibraryDocument) => {
      onWorkspaceDocumentsChange([...selectedWorkspaceDocuments, document]);
      onStatusMessage(
        `${document.originalFilename ?? document.filename} added to Selected Workspace Documents`,
      );
    },
    [selectedWorkspaceDocuments, onWorkspaceDocumentsChange, onStatusMessage],
  );

  const formatUploadDate = (timestamp?: number) => {
    if (!timestamp) return "Unknown date";
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const availableDocuments = useMemo(
    () =>
      workspaceDocuments.filter(
        (doc) =>
          doc.source !== "kc" &&
          !selectedWorkspaceDocuments.some((s) => s.filename === doc.filename),
      ),
    [workspaceDocuments, selectedWorkspaceDocuments],
  );

  return (
    <>
      <div className={styles.descriptionBox}>
        <p className={styles.descriptionBoxText}>
          Select a document when it warrants closer attention than a general
          search provides. A selected document is consulted directly and in full
          wherever length allows, and it takes priority over general workspace
          search.
        </p>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Selected Workspace Documents
          </h3>
        </div>

        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendSwatchTeal} aria-hidden="true" />
            Workspace document
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendSwatchAmber} aria-hidden="true" />
            Uploaded in this chat
          </span>
        </div>

        <div className={styles.focusTray}>
          {selectedWorkspaceDocuments.length === 0 &&
          (uploadedDocuments ?? []).length === 0 ? (
            <span className={styles.emptySelectedState}>
              Nothing selected. Workspace search still covers everything below.
            </span>
          ) : (
            <>
              {selectedWorkspaceDocuments.map((document) => (
                <div
                  key={document.filename}
                  className={mergeClasses(
                    styles.focusChipBase,
                    styles.focusChipWorkspace,
                  )}
                >
                  <DocumentText16Regular
                    className={styles.selectedTagIconLarge}
                    aria-hidden="true"
                  />
                  <span>{document.originalFilename}</span>
                  <span
                    className={mergeClasses(
                      styles.chipTypeBadge,
                      styles.chipTypeBadgeWorkspace,
                    )}
                  >
                    Workspace
                  </span>
                  <button
                    className={mergeClasses(
                      closeButtonStyles.closeButton,
                      styles.removeButton,
                    )}
                    onClick={() =>
                      handleRemoveWorkspaceDocument(document.filename)
                    }
                    aria-label={`Remove workspace document ${document.originalFilename} from selection`}
                  >
                    <Dismiss20Regular className={styles.removeTagIcon} />
                  </button>
                </div>
              ))}
              {(uploadedDocuments ?? []).map((document) => (
                <div
                  key={document.id ?? document.filename}
                  className={mergeClasses(
                    styles.focusChipBase,
                    styles.focusChipUploaded,
                  )}
                >
                  <DocumentText16Regular
                    className={styles.selectedTagIconLarge}
                    aria-hidden="true"
                  />
                  <span>{document.filename}</span>
                  <span
                    className={mergeClasses(
                      styles.chipTypeBadge,
                      styles.chipTypeBadgeUploaded,
                    )}
                  >
                    Uploaded in chat
                  </span>
                  <button
                    className={mergeClasses(
                      closeButtonStyles.closeButton,
                      styles.removeButton,
                    )}
                    onClick={() => handleRemoveUploadedDocument(document)}
                    aria-label={`Remove uploaded file ${document.filename}`}
                  >
                    <Dismiss20Regular className={styles.removeTagIcon} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div
          className={mergeClasses(
            sectionStyles.sectionHeader,
            styles.sectionHeader,
          )}
        >
          <h3
            className={mergeClasses(
              sectionStyles.sectionTitle,
              styles.sectionTitle,
            )}
          >
            Available Workspace Documents
          </h3>
        </div>
        {isLoadingWorkspaceDocuments ? (
          <div className={styles.loadingContainer}>
            <Spinner label="Loading workspace documents..." size="medium" />
          </div>
        ) : (
          <div className={styles.availableItemsList}>
            {availableDocuments.length === 0 ? (
              <span className={styles.emptySelectedState}>
                All workspace documents are selected.
              </span>
            ) : (
              availableDocuments.map((document) => (
                <div
                  key={document.filename}
                  className={styles.docRowButton}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleToggleWorkspaceDocument(document)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleToggleWorkspaceDocument(document);
                    }
                  }}
                  aria-label={`Add ${document.originalFilename} to selection`}
                >
                  <div className={styles.documentInfo}>
                    <DocumentText20Regular
                      className={styles.documentIconLarge}
                      aria-hidden="true"
                    />
                    <div className={styles.documentDetails}>
                      <div className={styles.selectableItemText}>
                        {document.originalFilename}
                      </div>
                      <div className={textStyles.caption}>
                        Uploaded {formatUploadDate(document.uploadedAt)}
                      </div>
                    </div>
                  </div>
                  <Add12Regular aria-hidden="true" />
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};

export const WorkspaceManageContent: React.FC<IWorkspaceManageContentProps> = ({
  isOpen,
  isPersonalWorkspace,
  onClose,
  knowledgeComponents,
  selectedKnowledgeComponents,
  onKnowledgeComponentsChange,
  workspaceDocuments,
  selectedWorkspaceDocuments,
  onWorkspaceDocumentsChange,
  uploadedDocuments,
  onUploadedDocumentsChange,
  workspaceId,
  onWorkspaceDocumentsRefresh,
  isLoadingKnowledgeComponents,
  isLoadingWorkspaceDocuments,
}) => {
  const styles = useWorkspaceManageContentStyles();
  const sharedModalStyles = useSharedModalStyles();
  const badgeStyles = useSharedBadgeStyles();
  const buttonStyles = useSharedButtonStyles();
  const [activeTab, setActiveTab] = useState<string>(
    WORKSPACE_MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS,
  );
  const [statusMessage, setStatusMessage] = useState<string>("");
  const tabListRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<Element | null>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      activeTab === WORKSPACE_MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS &&
      isPersonalWorkspace
    ) {
      setActiveTab(WORKSPACE_MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS);
    }
  }, [activeTab, isPersonalWorkspace]);

  useEffect(() => {
    if (isOpen) {
      triggerElementRef.current = document.activeElement;
      modalContentRef.current?.focus();
    } else if (triggerElementRef.current instanceof HTMLElement) {
      triggerElementRef.current.focus();
      triggerElementRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !modalContentRef.current) return;

      const focusableSelectors = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])",
        "[contenteditable]",
      ].join(", ");

      const focusableElements = Array.from(
        modalContentRef.current.querySelectorAll<HTMLElement>(
          focusableSelectors,
        ),
      ).filter((el) => !el.closest("[aria-hidden='true']"));

      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("keydown", handleTabKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleTabKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const [tabsOverflow, setTabsOverflow] = useState(false);

  useEffect(() => {
    const el = tabListRef.current;
    if (!el || !isOpen) return;
    const check = () => setTabsOverflow(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    isOpen,
    selectedKnowledgeComponents.length,
    selectedWorkspaceDocuments.length,
  ]);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabListRef.current) {
      const scrollAmount = 150;
      const currentScroll = tabListRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? Math.max(0, currentScroll - scrollAmount)
          : currentScroll + scrollAmount;

      tabListRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  const tabContentProps: ITabContentProps & {
    onStatusMessage?: (message: string) => void;
  } = {
    knowledgeComponents,
    selectedKnowledgeComponents,
    onKnowledgeComponentsChange,
    workspaceDocuments,
    selectedWorkspaceDocuments,
    onWorkspaceDocumentsChange,
    uploadedDocuments,
    onUploadedDocumentsChange,
    workspaceId,
    onWorkspaceDocumentsRefresh,
    isLoadingKnowledgeComponents,
    isLoadingWorkspaceDocuments,
    onStatusMessage: setStatusMessage,
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case WORKSPACE_MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS:
        return <KnowledgeComponentsTab {...tabContentProps} />;
      case WORKSPACE_MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS:
        return <WorkspaceDocumentsTab {...tabContentProps} />;
      default:
        return <WorkspaceDocumentsTab {...tabContentProps} />;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className={mergeClasses(
          sharedModalStyles.overlay,
          sharedModalStyles.overlayActive,
        )}
        onClick={onClose}
      >
        <div
          ref={modalContentRef}
          className={mergeClasses(
            sharedModalStyles.modal,
            sharedModalStyles.modalActive,
            styles.modalContentOverride,
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="workspace-manage-content-title"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <header
            className={mergeClasses(
              sharedModalStyles.modalHeader,
              styles.modalHeaderOverride,
            )}
          >
            <h2
              id="workspace-manage-content-title"
              className={sharedModalStyles.modalTitle}
            >
              Content Sources
            </h2>
            <Button
              ref={closeButtonRef}
              icon={<Dismiss20Regular />}
              appearance="subtle"
              onClick={onClose}
              aria-label="Close Content Sources dialog"
            />
          </header>

          <div className={styles.tabNavigation}>
            {tabsOverflow && (
              <Button
                icon={<ChevronLeft20Regular />}
                appearance="subtle"
                onClick={() => scrollTabs("left")}
                className={mergeClasses(
                  buttonStyles.ghostButton,
                  styles.tabScrollButton,
                  styles.tabScrollButtonLeft,
                )}
                aria-label="Scroll tabs left"
              />
            )}

            <div
              ref={tabListRef}
              className={mergeClasses(
                styles.tabListContainer,
                tabsOverflow && styles.tabListContainerOverflow,
              )}
            >
              <TabList
                selectedValue={activeTab}
                onTabSelect={(_, data) => setActiveTab(data.value as string)}
                className={styles.tabList}
              >
                {isPersonalWorkspace ? (
                  <></>
                ) : (
                  <Tab
                    value={WORKSPACE_MANAGE_CONTENT_TABS.WORKSPACE_DOCUMENTS}
                    className={styles.tab}
                  >
                    <div className={styles.tabContent}>
                      <DocumentFolder16Regular />
                      <span>Workspace Documents</span>
                      {selectedWorkspaceDocuments.length +
                        (uploadedDocuments?.length ?? 0) >
                        0 && (
                        <Badge
                          className={mergeClasses(
                            badgeStyles.badge,
                            styles.tabBadgeMargin,
                          )}
                          appearance="filled"
                          color="brand"
                        >
                          {selectedWorkspaceDocuments.length +
                            (uploadedDocuments?.length ?? 0)}
                        </Badge>
                      )}
                    </div>
                  </Tab>
                )}
                <Tab
                  value={WORKSPACE_MANAGE_CONTENT_TABS.KNOWLEDGE_COMPONENTS}
                  className={styles.tab}
                >
                  <div className={styles.tabContent}>
                    <DocumentText16Regular />
                    <span>Knowledge Components</span>

                    {selectedKnowledgeComponents.length > 0 && (
                      <Badge
                        className={mergeClasses(
                          badgeStyles.badge,
                          styles.tabBadgeMargin,
                        )}
                        appearance="filled"
                        color="brand"
                      >
                        {selectedKnowledgeComponents.length}
                      </Badge>
                    )}
                  </div>
                </Tab>
                <div className={styles.tabSpacer}></div>
              </TabList>
            </div>

            {tabsOverflow && (
              <Button
                icon={<ChevronRight20Regular />}
                appearance="subtle"
                onClick={() => scrollTabs("right")}
                className={mergeClasses(
                  buttonStyles.ghostButton,
                  styles.tabScrollButton,
                )}
                aria-label="Scroll tabs right"
              />
            )}
          </div>

          <div ref={tabContentRef} className={styles.tabContentArea}>
            {renderTabContent()}
            <div
              aria-live="polite"
              className={styles.visuallyHidden}
              aria-atomic="true"
            >
              {statusMessage}
            </div>
          </div>

          <footer className={sharedModalStyles.modalFooter}>
            <Button
              className={styles.doneButton}
              appearance="primary"
              onClick={onClose}
            >
              Done
            </Button>
          </footer>
        </div>
      </div>
    </>
  );
};
